import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Clears every scroll-lock style the theme may leave on <html>/<body>.
 * Cart drawer uses position:fixed on body; custom modals use overflow:hidden.
 * Mobile Back / bfcache (Chrome, Edge, Safari) can restore a locked page after ATC.
 *
 * @param {number} [scrollY]
 */
export function clearDialogScrollLock(scrollY) {
  const body = document.body;
  const html = document.documentElement;
  if (!body || !html) return;

  const top = body.style.top;
  const locked =
    body.style.position === 'fixed' ||
    body.style.top !== '' ||
    body.style.width !== '' ||
    body.style.overflow === 'hidden' ||
    html.style.overflow === 'hidden' ||
    html.hasAttribute('scroll-lock');

  if (!locked) return;

  const y =
    typeof scrollY === 'number' && Number.isFinite(scrollY)
      ? scrollY
      : top
        ? Math.abs(parseInt(top, 10)) || 0
        : window.scrollY || window.pageYOffset || 0;

  body.style.width = '';
  body.style.position = '';
  body.style.top = '';
  body.style.left = '';
  body.style.right = '';
  body.style.overflow = '';
  html.style.overflow = '';
  html.removeAttribute('scroll-lock');

  window.scrollTo(0, y);
}

/**
 * Close any open dialogs then unlock scroll (bfcache / Back recovery).
 */
export function recoverPageScroll() {
  document.querySelectorAll('dialog[open]').forEach((dialog) => {
    try {
      dialog.close();
    } catch (_) {
      /* ignore */
    }
    dialog.classList.remove('dialog-closing');
  });
  clearDialogScrollLock();
}

/**
 * @returns {boolean}
 */
function hasOpenDialog() {
  return Boolean(document.querySelector('dialog[open]'));
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** @type {boolean} Whether an open is already scheduled this frame. */
  #isOpening = false;

  /** @type {boolean} Prevent overlapping close animations. */
  #isClosing = false;

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    // Guard against both `dialog.open` and a concurrent open already scheduled
    // this frame (e.g. a direct open() call racing the auto-open handler), which
    // would otherwise call showModal() twice and throw InvalidStateError.
    if (dialog.open || this.#isOpening) return;
    this.#isOpening = true;
    this.#isClosing = false;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      this.#isOpening = false;

      if (dialog.open) return;

      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    // Unlock immediately — never wait on animation before restoring scroll.
    // This is the main fix for “Back after ATC → page stuck”.
    clearDialogScrollLock(this.#previousScrollY);

    if (!dialog.open) {
      this.#isClosing = false;
      return;
    }

    if (this.#isClosing) return;
    this.#isClosing = true;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    try {
      await Promise.race([
        onAnimationEnd(dialog, undefined, {
          subtree: false,
        }),
        new Promise((resolve) => setTimeout(resolve, 400)),
      ]);
    } finally {
      clearDialogScrollLock(this.#previousScrollY);

      if (dialog.open) {
        try {
          dialog.close();
        } catch (_) {
          /* ignore */
        }
      }
      dialog.classList.remove('dialog-closing');
      this.#isClosing = false;

      this.dispatchEvent(new DialogCloseEvent());
    }
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

/** bfcache restore (Safari / Chrome / Edge) */
window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    recoverPageScroll();
    return;
  }
  // Soft navigations can also leave overflow:hidden — clear if nothing is open.
  if (!hasOpenDialog()) clearDialogScrollLock();
});

/** Prefer storing an unlocked body in bfcache */
window.addEventListener('pagehide', () => {
  clearDialogScrollLock();
});

/**
 * Mobile cart drawer pushes history; Back fires popstate.
 * Unlock synchronously here so scroll works even if closeDialog is delayed.
 */
window.addEventListener('popstate', () => {
  clearDialogScrollLock();
});
